import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Observable, retry, throwError, timeout, TimeoutError, timer } from 'rxjs';
import { TelemetryService } from '#services';
import { HTTP_TIMEOUT_MS } from '../http-timeout.token';

export const HTTP_RETRY_BASE_DELAY_MS = 1_000;
export const HTTP_RETRY_JITTER_MS = 500;
export const HTTP_RETRY_TRACE_MESSAGE = 'HTTP request retried after a transient failure';

type RetryReason = 'timeout' | 'network';

/**
 * Bounds each `/api` attempt with HTTP_TIMEOUT_MS and retries a GET once after a
 * timeout or a network failure (status 0). Other methods and HTTP error
 * responses are never retried.
 *
 * Registered AFTER the MsalInterceptor so a retry reuses the acquired token and
 * the token wait doesn't count toward the per-attempt timeout.
 */
@Injectable()
export class HttpResilienceInterceptor implements HttpInterceptor {
  private _telemetry = inject(TelemetryService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.includes('/api')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      timeout(req.context.get(HTTP_TIMEOUT_MS)),
      retry({
        count: req.method === 'GET' ? 1 : 0,
        delay: (error: unknown, attempt: number) => this.retryDelay(error, attempt),
      })
    );
  }

  private retryDelay(error: unknown, attempt: number): Observable<number> {
    const reason = transientReason(error);
    if (!reason) {
      return throwError(() => error);
    }

    const delayMs = Math.round(HTTP_RETRY_BASE_DELAY_MS + Math.random() * HTTP_RETRY_JITTER_MS);
    // Fixed message and allowlisted properties only: never the URL, query, headers, or error text.
    this._telemetry.trackTrace({
      message: HTTP_RETRY_TRACE_MESSAGE,
      severityLevel: SeverityLevel.Warning,
      properties: { reason, attempt, delayMs },
    });
    return timer(delayMs);
  }
}

function transientReason(error: unknown): RetryReason | null {
  if (error instanceof TimeoutError) {
    return 'timeout';
  }
  if (error instanceof HttpErrorResponse && error.status === 0) {
    return 'network';
  }
  return null;
}
